import type {CommentBoxHandle} from '@github-ui/comment-box/CommentBox'
import {ConversationCommentBox, type ConversationCommentBoxProps} from '@github-ui/diff-lines/conversation-comment-box'
import {useCallback, useEffect, useState} from 'react'

import {useCopilotQuotaGate} from '../toolbar/use-copilot-quota-gate'

/**
 * ControlledCommentBox is a wrapper around ConversationCommentBox that allows
 * it to be controlled by an initial value and an onChange callback.
 * This is useful for cases where you want to manage the comment box state
 * outside of the component to prevent unnecessary re-renders on every keystroke.
 *
 * It also runs the Copilot quota gate against the current text, rendering the
 * banner above the comment box and reporting the submit-disabled signal through
 * `onQuotaSubmitDisabledChange`.
 */
export const ControlledCommentBox = ({
  ref,
  initialValue,
  onChange,
  onQuotaSubmitDisabledChange,
  ...props
}: Omit<ConversationCommentBoxProps, 'value'> & {
  initialValue: string
  onChange: (value: string) => void
  onQuotaSubmitDisabledChange?: (disabled: boolean) => void
} & {ref?: React.Ref<CommentBoxHandle>}) => {
  const [reviewBody, setReviewBody] = useState<string>(initialValue)

  const onCommentBoxChange = useCallback(
    (value: string) => {
      setReviewBody(value)
      onChange(value)
    },
    [setReviewBody, onChange],
  )

  const {banner: quotaBanner, submitDisabled: quotaSubmitDisabled} = useCopilotQuotaGate(reviewBody)

  useEffect(() => {
    onQuotaSubmitDisabledChange?.(quotaSubmitDisabled)
  }, [quotaSubmitDisabled, onQuotaSubmitDisabledChange])

  return (
    <>
      {quotaBanner}
      <ConversationCommentBox {...props} value={reviewBody} onChange={onCommentBoxChange} ref={ref} />
    </>
  )
}

ControlledCommentBox.displayName = 'ControlledCommentBox'
